/**
 * Import CSV dei soci con il loro profilo energetico.
 *
 * Una riga = un socio. Le colonne dell'anagrafica sono quelle di prima; quelle
 * energetiche sono facoltative e, se ci sono, finiscono sui punti di misura che
 * il ruolo crea (vedi `POST /api/members`, campo `profiloEnergetico`).
 *
 * Il separatore si riconosce dall'intestazione: `;` è quello che Excel in
 * italiano scrive, perché la virgola è il separatore dei decimali.
 */

import {
	APPLIANCE_TYPES,
	type ApplianceType,
	type MemberAppliance,
	normalizeAppliances,
} from '../energy/appliances';
import type { AdminEnergyProfilePatch, TipologiaUtenza } from '../energy/energy-profile';
import type { MemberRole, MemberType } from '../types/member';

/** Le colonne riconosciute, nell'ordine del modello, con cosa contengono. */
export const MEMBER_CSV_COLUMNS = [
	['nome', 'Obbligatorio'],
	['tipo', 'famiglia, ufficio, negozio, impresa, fabbrica, ente pubblico, associazione'],
	['ruolo', 'Obbligatorio: consumatore, produttore, prosumer'],
	['email', 'Per l’invito al portale'],
	['pod', 'Codice POD, es. IT001E12345678: per un prosumer vale per prelievo e immissione'],
	['tipo_utenza', 'domestico, altri_usi, ricarica_veicoli'],
	['potenza_kwp', 'Potenza dell’impianto in kWp'],
	['data_esercizio', 'Entrata in esercizio, AAAA-MM-GG o GG/MM/AAAA'],
	['numero_moduli', ''],
	['potenza_modulo_w', 'Potenza del singolo modulo in W'],
	['potenza_inverter_kw', ''],
	['accumulo', 'sì / no — solo per produttore e prosumer'],
	['capacita_batteria_kwh', ''],
	['potenza_batteria_kw', ''],
	[
		'dispositivi',
		'Separati da |, es. «pompa di calore 1x 3kW | climatizzatore 2x» — solo con un prelievo',
	],
] as const;

/** Il modello da scaricare: intestazione e una riga d'esempio, separati da `;`. */
export const MEMBER_CSV_TEMPLATE = [
	MEMBER_CSV_COLUMNS.map(([c]) => c).join(';'),
	[
		'Mario Rossi',
		'famiglia',
		'prosumer',
		'mario.rossi@example.it',
		'IT001E12345678',
		'domestico',
		'4',
		'2023-06-01',
		'10',
		'400',
		'3,6',
		'sì',
		'10',
		'5',
		'pompa di calore 1x 3kW | climatizzatore 2x',
	].join(';'),
].join('\n');

function splitRow(line: string, sep: string): string[] {
	const out: string[] = [];
	let cur = '';
	let quoted = false;
	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (ch === '"') {
			if (quoted && line[i + 1] === '"') {
				cur += '"';
				i++;
			} else quoted = !quoted;
		} else if (ch === sep && !quoted) {
			out.push(cur);
			cur = '';
		} else cur += ch;
	}
	out.push(cur);
	return out.map((v) => v.trim());
}

/** Le righe del file come oggetti `colonna → valore`, con le intestazioni in minuscolo. */
export function parseMemberCsv(text: string): Record<string, string>[] {
	const lines = text
		.replace(/^﻿/, '')
		.split(/\r?\n/)
		.filter((l) => l.trim() !== '');
	if (lines.length === 0) return [];
	const first = lines[0]!;
	const sep = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : ',';
	const header = splitRow(first, sep).map((h) => h.toLowerCase());
	return lines.slice(1).map((line) => {
		const values = splitRow(line, sep);
		return Object.fromEntries(header.map((h, i) => [h, values[i] ?? '']));
	});
}

/** Il tipo di socio, per etichetta italiana o per codice. */
const TIPI: Record<string, MemberType> = {
	famiglia: 'residential',
	ufficio: 'office',
	negozio: 'shop',
	impresa: 'sme',
	fabbrica: 'factory',
	'ente pubblico': 'public_administration',
	associazione: 'ets',
	...Object.fromEntries(
		(
			['residential', 'office', 'shop', 'sme', 'factory', 'public_administration', 'ets'] as const
		).map((t) => [t, t]),
	),
};

const RUOLI: Record<string, MemberRole> = {
	consumatore: 'consumer',
	produttore: 'producer',
	prosumer: 'prosumer',
	consumer: 'consumer',
	producer: 'producer',
};

const TIPOLOGIE: Record<string, TipologiaUtenza> = {
	domestico: 'domestico',
	domestica: 'domestico',
	altri_usi: 'altri_usi',
	'altri usi': 'altri_usi',
	ricarica_veicoli: 'ricarica_veicoli',
	'ricarica veicoli': 'ricarica_veicoli',
	'ricarica veicoli elettrici': 'ricarica_veicoli',
};

const APPLIANCE_BY_NAME: Record<string, ApplianceType> = Object.fromEntries(
	APPLIANCE_TYPES.flatMap((t) => [
		[t.value, t.value],
		[t.value.replace(/_/g, ' '), t.value],
		[t.label.toLowerCase(), t.value],
		...t.label
			.toLowerCase()
			.split(/\s*\/\s*|\s+o\s+/)
			.map((part) => [part, t.value]),
	]),
);

/**
 * «pompa di calore 1x 3kW | climatizzatore 2x | sauna»: un dispositivo per
 * pezzo, con quanti (`2x`, uno se manca) e la potenza (`3kW`, facoltativa). Un
 * nome che non è nel catalogo diventa «altro» con quel nome.
 */
function parseDispositivi(raw: string): MemberAppliance[] | string {
	const items: Record<string, unknown>[] = [];
	for (const piece of raw
		.split('|')
		.map((p) => p.trim())
		.filter(Boolean)) {
		const m = /^(.*?)\s*(?:(\d+)\s*x)?\s*(?:(\d+(?:[.,]\d+)?)\s*kw)?\s*$/i.exec(piece);
		const nome = (m?.[1] ?? piece).trim();
		const tipo = APPLIANCE_BY_NAME[nome.toLowerCase()] ?? 'altro';
		items.push({
			tipo,
			...(tipo === 'altro' ? { nome } : {}),
			quantita: m?.[2] ? Number(m[2]) : 1,
			potenzaKw: m?.[3] ? Number(m[3].replace(',', '.')) : null,
		});
	}
	const r = normalizeAppliances(items);
	return 'error' in r ? r.error : r.value;
}

/** «3,6» e «3.6» sono lo stesso numero; «1.200,5» anche. */
function parseNumber(raw: string): number | null | 'invalid' {
	const t = raw.trim();
	if (!t) return null;
	const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
	const n = Number(normalized);
	return Number.isFinite(n) ? n : 'invalid';
}

function parseDate(raw: string): string | null | 'invalid' {
	const t = raw.trim();
	if (!t) return null;
	if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
	const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
	if (m) return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
	return 'invalid';
}

export interface MemberCsvRow {
	/** Campi del socio per `POST /api/members`. */
	member: Record<string, unknown>;
	/** Perché la riga non si può importare (es. ruolo mancante), o `null`. */
	errore: string | null;
	/** Profilo energetico, se la riga ne ha almeno un campo. */
	profilo: AdminEnergyProfilePatch | null;
	/** Valori non riconosciuti: la riga si importa senza di loro, e si dice quali. */
	avvisi: string[];
}

/**
 * Una riga del CSV nel socio e nel suo profilo. Non rifiuta la riga per un
 * valore energetico sbagliato: lo scarta e lo segnala, perché il socio senza
 * quel dato è comunque meglio di nessun socio.
 */
export function memberCsvRow(row: Record<string, string>): MemberCsvRow {
	const avvisi: string[] = [];
	const get = (k: string) => (row[k] ?? '').trim();

	const num = (k: string, label: string, opts: { int?: boolean } = {}) => {
		const v = parseNumber(get(k));
		if (v === 'invalid' || (v != null && (!(v > 0) || (opts.int && !Number.isInteger(v))))) {
			avvisi.push(`${label} non valido («${get(k)}»)`);
			return null;
		}
		return v;
	};

	// Il ruolo decide i punti di misura: senza, la riga non si importa.
	const ruolo = RUOLI[get('ruolo').toLowerCase()] ?? null;
	const errore = ruolo
		? null
		: get('ruolo')
			? `ruolo non riconosciuto («${get('ruolo')}»)`
			: 'manca il ruolo';

	const tipoRaw = get('tipo').toLowerCase();
	const tipo = tipoRaw ? (TIPI[tipoRaw] ?? null) : 'residential';
	if (!tipo) avvisi.push(`Tipo non riconosciuto («${get('tipo')}»): importato come famiglia`);

	const accumuloRaw = get('accumulo').toLowerCase();
	let hasStorage = ['sì', 'si', 'yes', 'true', '1', 'x'].includes(accumuloRaw);
	// L'accumulo sta accanto a un impianto: un consumatore puro non ne ha.
	if (hasStorage && ruolo === 'consumer') {
		avvisi.push('Accumulo ignorato: il socio è consumatore');
		hasStorage = false;
	}

	const member: Record<string, unknown> = {
		nome: get('nome'),
		tipo: tipo ?? 'residential',
		ruolo,
		email: get('email') || null,
	};
	if (hasStorage) member.hasStorage = true;

	// I dispositivi stanno sul prelievo: un produttore puro non ne ha.
	if (get('dispositivi')) {
		const parsed = parseDispositivi(get('dispositivi'));
		if (typeof parsed === 'string') avvisi.push(`Dispositivi ignorati: ${parsed}`);
		else if (ruolo === 'producer') avvisi.push('Dispositivi ignorati: il socio è solo produttore');
		else member.dispositivi = parsed;
	}

	const tipologiaRaw = get('tipo_utenza').toLowerCase();
	const tipologia = tipologiaRaw ? (TIPOLOGIE[tipologiaRaw] ?? null) : null;
	if (tipologiaRaw && !tipologia)
		avvisi.push(`Tipo di utenza non riconosciuto («${get('tipo_utenza')}»)`);

	const data = parseDate(get('data_esercizio'));
	if (data === 'invalid') avvisi.push(`Data di esercizio non valida («${get('data_esercizio')}»)`);

	// Un solo POD per riga: è lo stesso contatore per prelievo e immissione. Il
	// produttore non preleva, quindi il codice va solo sul punto di immissione.
	const pod = get('pod') ? get('pod').toUpperCase() : null;
	const preleva = ruolo !== 'producer';
	if (!preleva && tipologia) avvisi.push('Tipo di utenza ignorato: il socio è solo produttore');
	const consumo = preleva
		? { ...(pod ? { codice: pod } : {}), ...(tipologia ? { tipologiaUtenza: tipologia } : {}) }
		: {};
	// Senza potenza dichiarata, i moduli la danno: è la stessa somma che usano le curve.
	const moduli = num('numero_moduli', 'Numero moduli', { int: true });
	const moduloW = num('potenza_modulo_w', 'Potenza modulo');
	const kwp =
		num('potenza_kwp', 'Potenza impianto') ??
		(moduli != null && moduloW != null ? Math.round(moduli * moduloW) / 1000 : null);
	const produzione = Object.fromEntries(
		Object.entries({
			codice: ruolo === 'consumer' ? null : pod,
			potenzaKwp: kwp,
			dataEntrataEsercizio: data === 'invalid' ? null : data,
			numeroModuli: moduli,
			potenzaModuloW: moduloW,
			potenzaInverterKw: num('potenza_inverter_kw', 'Potenza inverter'),
		}).filter(([, v]) => v != null),
	);
	const batteria = Object.fromEntries(
		Object.entries({
			capacitaKwh: num('capacita_batteria_kwh', 'Capacità batteria'),
			potenzaKw: num('potenza_batteria_kw', 'Potenza batteria'),
		}).filter(([, v]) => v != null),
	);
	const hasBatteria = Object.keys(batteria).length > 0;
	if (hasBatteria && !hasStorage && !avvisi.some((a) => a.startsWith('Accumulo ignorato')))
		avvisi.push(
			ruolo === 'consumer'
				? 'Dati della batteria ignorati: il socio è consumatore'
				: 'Dati della batteria ignorati: manca «sì» nella colonna accumulo',
		);
	const accumulo = hasStorage ? batteria : {};

	const profilo: AdminEnergyProfilePatch = {};
	if (Object.keys(consumo).length > 0) profilo.consumo = consumo;
	if (Object.keys(produzione).length > 0) profilo.produzione = produzione;
	if (Object.keys(accumulo).length > 0) profilo.accumulo = accumulo;

	return { member, errore, profilo: Object.keys(profilo).length > 0 ? profilo : null, avvisi };
}
